import { TeamDB } from "@aura-frontend/omnibus-az-client";
import type { ColumnDef, Row } from "@tanstack/react-table";
import { ExpandedState } from "@tanstack/react-table";
import { Table } from "@win-reality/react-ui";
import { useState } from "react";

import { RosterPlayersTable } from "./RosterPlayersTable";
import { useRostersColumns } from "./RostersTable.columns";

type RostersTableProps = {
  data: TeamDB[];
  pageSize: number;
  isFetching: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  orgId: string;
};

function RostersTable({
  data,
  isFetching,
  pageSize,
  hasNextPage,
  fetchNextPage,
  orgId: _orgId,
}: RostersTableProps) {
  const [expandedState, setExpanded] = useState<ExpandedState>({});

  const { rostersColumns } = useRostersColumns({ orgId: _orgId });

  return (
    <div>
      <Table
        enableHorizontalScroll
        hideHeader
        variant="infinite-scroll"
        hasMore={hasNextPage}
        isFetching={isFetching}
        columns={
          rostersColumns as unknown as ColumnDef<Record<string, unknown>>[]
        }
        data={data as unknown as Record<string, unknown>[]}
        paginationState={{
          pageSize,
          pageIndex: 0,
        }}
        onEndReached={() => {
          if (hasNextPage && fetchNextPage) {
            fetchNextPage();
          }
        }}
        expandedState={expandedState}
        onExpandedChange={setExpanded}
        onRowClick={(row) => {
          if (row.getCanExpand()) {
            row.toggleExpanded();
          }
        }}
        tableWrapperClassName="overflow-auto px-5 py-1"
        rowsWrapperClassName="gap-y-4"
        rowClassName={"group shadow-glow-tiny rounded"}
        expandedContentClassName="bg-white border-t border-[hsla(240,11%,61%,0.2)] px-0"
        renderExpandableRowComponent={(row: Row<Record<string, unknown>>) => {
          const externalId = (row.original as TeamDB).external_ids?.[0];
          if (!externalId) {
            return null;
          }

          return (
            <div className="persistent-small-scrollbar max-h-100 min-w-0 overflow-auto contain-[inline-size]">
              <RosterPlayersTable
                rosterId={externalId}
                unofficialMembers={
                  (row.original as TeamDB).unofficial_members ?? []
                }
              />
            </div>
          );
        }}
      />
    </div>
  );
}

export { RostersTable };
